import React, { ReactElement } from "react";

import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../context/StateContext";
import { ResourceGroup, ResourceName, ResourceSubGroup } from "../../../../util/enums";
import { getResourceByNameGroup } from "../../../../util/getResourceByNameGroup";
import { Button } from "../../../Form";
import LoadingSkeleton from "../../../Generic/LoadingSkeleton";
import SideView from "../../../Generic/SideView";
import { ViewTab } from "../../../Generic/SideView/types";
import { useSupplyChainOverviewContext } from "../context";
import ContainersConfig from "./Containers";
import ProductsConfig from "./Products";
import PurchaseOrdersConfig from "./PurchaseOrders";
import ShipmentsConfig from "./Shipments";

interface Props {
  isOpened: boolean;
  setIsOpened: (value: boolean) => void;
}

export const ConfigureSupplyChainOverview = ({ isOpened, setIsOpened }: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const { resetConfigToDefaut } = useSupplyChainOverviewContext();

  const footerComponent: ReactElement = (
    <Stack direction="row" justifyContent="flex-end" spacing={2} width="100%">
      <Button onClick={() => resetConfigToDefaut()}>{translate("resetToDefault")}</Button>
    </Stack>
  );

  const containersPermission = getResourceByNameGroup(
    ResourceGroup.CONTAINERS,
    ResourceSubGroup.CONTAINERS,
    ResourceName.CONTAINERS,
    currentUser?.resources
  );

  const ordersPermission = getResourceByNameGroup(
    ResourceGroup.ORDERS,
    ResourceSubGroup.ORDERS,
    ResourceName.ORDERS,
    currentUser?.resources
  );

  const productsPermission = getResourceByNameGroup(
    ResourceGroup.BUSINESS,
    ResourceSubGroup.BUSINESS,
    ResourceName.PRODUCTS,
    currentUser?.resources
  );

  const shipmentsPermission = getResourceByNameGroup(
    ResourceGroup.SHIPMENTS,
    ResourceSubGroup.SHIPMENTS,
    ResourceName.SHIPMENTS,
    currentUser?.resources
  );

  const tabs: ViewTab[] = [];

  if (ordersPermission?.roles_resources?.view) {
    tabs.push({
      isEmpty: false,
      isOpened: false,
      key: "orders",
      label: translate("orders"),
      loading: false,
      render: () => <PurchaseOrdersConfig />,
      renderLoading: () => <LoadingSkeleton />,
    });
  }
  if (productsPermission?.roles_resources?.view) {
    tabs.push({
      isEmpty: false,
      isOpened: false,
      key: "products",
      label: translate("products"),
      loading: false,
      render: () => <ProductsConfig />,
      renderLoading: () => <LoadingSkeleton />,
    });
  }
  if (shipmentsPermission?.roles_resources?.view) {
    tabs.push({
      isEmpty: false,
      isOpened: false,
      key: "shipments",
      label: translate("shipments"),
      loading: false,
      render: () => <ShipmentsConfig />,
      renderLoading: () => <LoadingSkeleton />,
    });
  }
  if (containersPermission?.roles_resources?.view) {
    tabs.push({
      isEmpty: false,
      isOpened: false,
      key: "containers",
      label: translate("containers"),
      loading: false,
      render: () => <ContainersConfig />,
      renderLoading: () => <LoadingSkeleton />,
    });
  }

  return (
    <SideView
      defaultTab={"orders"}
      footerComponent={footerComponent}
      isLoading={false}
      isOpened={isOpened}
      onClose={() => setIsOpened(false)}
      subtitle={translate("adjustWidgetsAndViewAsYouWish")}
      tabs={tabs}
      title={translate("configureOverview")}
      sideTitle={translate("widgets")}
    />
  );
};
